import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../services/hrmsService";
import { FormDrawer } from "../HR/hrForm";
import { EmptyState, LoadState, PageHeader, SecondaryButton, Tabs } from "../HR/hrComponents";
import { cardStyle, pageStyle, tdStyle, thStyle, todayYmd, useLoad } from "../HR/hrUi";

// Super Admin: the statutory rates every tenant's payroll reads (payroll gratuity, pension and so on), effective-dated.
// Nothing here is built into a calculation; a run refuses to work out something whose rate is not set.
const COUNTRIES = [{ key: "AE", label: "United Arab Emirates" }, { key: "IN", label: "India" }];

export default function StatutoryRates() {
  const [country, setCountry] = useState("AE");
  const { data, loading, error, reload } = useLoad(() => hrmsService.listStatutoryRates(country), [country]);
  const [editing, setEditing] = useState(undefined);
  const today = todayYmd();
  const known = data?.known || [];
  const rates = data?.rates || [];
  const historyOf = (key) => rates.filter((r) => r.rate_key === key);
  const nowOf = (key) => historyOf(key).filter((r) => String(r.effective_from).slice(0, 10) <= today)[0];

  const seed = async () => {
    const res = await hrmsService.seedStatutoryDefaults();
    if (res?.success) {
      toast.success(res.message || "Added");
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <div style={pageStyle}>
      <PageHeader
        title="Statutory rates"
        subtitle="Effective-dated rates the payroll of every company reads"
        actions={
          country === "AE" ? (
            <SecondaryButton onClick={seed}>Add the figures from the HR spec</SecondaryButton>
          ) : null
        }
      />
      <Tabs tabs={COUNTRIES} active={country} onChange={setCountry} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {known.length === 0 ? (
          <EmptyState title="No rates for this country yet" text="Payroll for this country is not built yet." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 720 }}>
              <thead>
                <tr>
                  {["Rate", "Value now", "Since", "History", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {known.map((k) => {
                  const now = nowOf(k.key);
                  const older = historyOf(k.key).filter((r) => r !== now);
                  return (
                    <tr key={k.key}>
                      <td style={tdStyle}>
                        {k.description}
                        <div style={{ fontSize: 11, color: "#9CA3AF" }}>{k.key}</div>
                      </td>
                      <td style={tdStyle}>{now ? `${now.value_num}${k.unit === "PERCENT" ? " %" : ""}` : <span style={{ color: k.optional ? "#6B7280" : "#B91C1C" }}>{k.optional ? "Not set (optional)" : "Not set"}</span>}</td>
                      <td style={tdStyle}>{now ? String(now.effective_from).slice(0, 10) : "—"}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: "#6B7280" }}>{older.length ? older.map((r) => `${r.value_num} from ${String(r.effective_from).slice(0, 10)}`).join("; ") : "—"}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        <button type="button" className="btn btn-link btn-sm" onClick={() => setEditing(k)}>
                          Set
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={editing !== undefined}
        title={editing ? `Set: ${editing.description}` : "Set rate"}
        initial={{ effective_from: today }}
        fields={[
          { key: "effective_from", label: "Takes effect on", type: "date", required: true },
          { key: "value_num", label: `Value${editing && editing.unit === "PERCENT" ? " (%)" : ""}`, type: "number", step: "0.0001", min: "0", required: true },
          { key: "source_note", label: "Where the figure comes from", max: 300, hint: "The law, circular or contract, so it can be checked later" },
        ]}
        onHide={() => setEditing(undefined)}
        onSave={(clean) => hrmsService.setStatutoryRate({ ...clean, rate_key: editing.key })}
        onDone={reload}
      />
    </div>
  );
}
